import { PASSAGE_CONTEXT_MAX } from "@esi/shared";
import { QUOTE_MAX } from "./instructions.ts";

/** Context kept on each side of a quote (D11). */
export const CONTEXT_MAX = PASSAGE_CONTEXT_MAX;

/** A passage selected in the reading view: its text, its context and its section. */
export interface Passage {
  quote: string;
  prefix: string;
  suffix: string;
  /** The enclosing heading's text and anchor id, or null before the first heading. */
  section: { text: string; id: string | null } | null;
}

/**
 * What a reader sees of a fragment: removed text (struck in the Marked view), screen-reader
 * words and icons are not part of the page as it will read.
 */
function readText(fragment: DocumentFragment): string {
  for (const node of fragment.querySelectorAll(
    "del, .sr-only, [aria-hidden='true'], svg, [data-selection-ignore]",
  )) {
    node.remove();
  }
  return (fragment.textContent ?? "").replace(/\s+/g, " ");
}

/**
 * The passage `range` selects inside `container` (D11): the quote trimmed to 500 characters,
 * 64 characters of context on each side, and the nearest heading before it. Null when the
 * selection is empty or leaves the container.
 */
export function passageOf(container: HTMLElement, range: Range): Passage | null {
  if (
    range.collapsed ||
    !container.contains(range.commonAncestorContainer) ||
    // A selection inside the popover or the tray is not a passage of the page.
    (range.commonAncestorContainer instanceof Element
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement
    )?.closest("[data-selection-ignore]")
  ) {
    return null;
  }
  const quote = readText(range.cloneContents()).trim().slice(0, QUOTE_MAX);
  if (!quote) {
    return null;
  }
  const before = document.createRange();
  before.selectNodeContents(container);
  before.setEnd(range.startContainer, range.startOffset);
  const after = document.createRange();
  after.selectNodeContents(container);
  after.setStart(range.endContainer, range.endOffset);
  const prefix = readText(before.cloneContents()).trimStart().slice(-CONTEXT_MAX);
  const suffix = readText(after.cloneContents()).trimEnd().slice(0, CONTEXT_MAX);
  let section: Passage["section"] = null;
  for (const heading of container.querySelectorAll<HTMLElement>("h1, h2, h3, h4, h5, h6")) {
    // The surface's own title is not a section of the page.
    if (heading.closest("[data-selection-ignore]")) {
      continue;
    }
    // A heading that starts before the selection, the last one wins.
    if (range.comparePoint(heading, 0) < 0 || heading.contains(range.startContainer)) {
      const clone = document.createRange();
      clone.selectNodeContents(heading);
      section = { text: readText(clone.cloneContents()).trim(), id: heading.id || null };
    }
  }
  return { quote, prefix, suffix, section };
}

/** What the reading view skips when it looks for a passage (as `readText` does). */
const SKIPPED = "del, .sr-only, [aria-hidden='true'], svg, [data-selection-ignore]";

/**
 * The range where `quote` reads inside `container` (the first occurrence, whitespace ignored, as
 * the server checks it), or null. Struck text and screen-reader words are not part of the page.
 */
export function rangeOf(
  container: HTMLElement,
  quote: string,
  context?: { prefix: string; suffix: string },
): Range | null {
  // No normalisation here: `at` indexes the page's own code units, and the selection's quote
  // comes from the same DOM text.
  const needle = quote.replace(/\s+/g, "");
  if (!needle) {
    return null;
  }
  const chars: string[] = [];
  const at: { node: Text; offset: number }[] = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.parentElement?.closest(SKIPPED)) {
      continue;
    }
    const text = node.textContent ?? "";
    for (let i = 0; i < text.length; i++) {
      const c = text[i] as string;
      if (!/\s/.test(c)) {
        chars.push(c);
        at.push({ node: node as Text, offset: i });
      }
    }
  }
  const text = chars.join("");
  // The occurrence with the anchor's context around it, else the first (the server dropped
  // context it could not verify, so empty context matches anywhere).
  const prefix = (context?.prefix ?? "").replace(/\s+/g, "");
  const suffix = (context?.suffix ?? "").replace(/\s+/g, "");
  let index = text.indexOf(needle);
  for (let i = index; i >= 0; i = text.indexOf(needle, i + 1)) {
    if (text.endsWith(prefix, i) && text.startsWith(suffix, i + needle.length)) {
      index = i;
      break;
    }
  }
  const start = at[index];
  const end = at[index + needle.length - 1];
  if (index < 0 || !start || !end) {
    return null;
  }
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset + 1);
  return range;
}

/**
 * A passage's section as the server keys it: the heading's slug without the reading view's
 * prefix (`<prefix>-`) or a repeated heading's `--n`.
 */
export function sectionSlug(passage: Passage, prefix: string): string | null {
  const id = passage.section?.id;
  if (!id?.startsWith(`${prefix}-`)) {
    return null;
  }
  return id.slice(prefix.length + 1).replace(/--\d+$/, "") || null;
}
